import React from 'react';
import { Skeleton, SkeletonText, SkeletonPanel, SkeletonRegion } from './Skeleton';

/**
 * PageSkeleton
 * ------------
 * The neutral, route-agnostic page placeholder rendered by the auth and
 * subscription gates while they resolve.
 *
 * Why this exists: every protected route used to run a three-stage cascade —
 * RequireAuth returned `null` (white flash), then RequireSubscription showed
 * the bare string "Loading...", then the page's own spinner. Three different
 * empty states before any content. This is the first of the three, and it
 * holds the page's frame so the shell doesn't visibly rebuild underneath the
 * content when it finally lands.
 *
 * It deliberately does NOT try to guess the destination route's shape — a
 * wrong guess shifts the layout twice instead of once. Title, subtitle, and
 * a card grid is the shape most pages in this app share.
 */
export default function PageSkeleton({ label = 'Loading' }) {
  return (
    <SkeletonRegion
      label={label}
      style={{ maxWidth: 'var(--frame-dashboard)', margin: '0 auto', padding: '32px 0' }}
    >
      <Skeleton width="42%" height={30} style={{ marginBottom: 'var(--space-3)' }} />
      <Skeleton width="62%" height={15} style={{ marginBottom: 'var(--space-6)' }} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        {Array.from({ length: 6 }, (_, i) => (
          <SkeletonPanel key={i}>
            <Skeleton height={96} style={{ marginBottom: 'var(--space-3)' }} />
            <SkeletonText lines={2} />
          </SkeletonPanel>
        ))}
      </div>
    </SkeletonRegion>
  );
}
